/**
 * @file index.tsx
 *
 * @fileoverview This file contains the JobSearchBreadcrumbs component,
 * which is responsible for rendering the breadcrumbs for the job search page.
 * It uses the Breadcrumbs component and utility functions to generate the
 * breadcrumb items based on the provided props and using appropriate schema.
 */
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import Breadcrumbs from '~/jobiqo/components/Breadcrumbs';
import { Facet } from '~/jobiqo/models/search';
import { Job } from '~/jobiqo/types/types';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import {
  getJobSearchBreadcrumbItems,
  hasJobSearchBreadcrumbItems,
} from './utils';

interface JobSearchBreadcrumbsProps {
  /**
   * The URL of the jobs page, which is used to generate the first breadcrumb item.
   */
  jobsPage: string;
  /**
   * The facets used to filter the job search results, which are used to generate additional breadcrumb items.
   */
  facets?: Facet[] | null;
  /**
   * The query string parameters used to filter the job search results, which are used to generate additional breadcrumb items.
   */
  querystring?: Record<string, string | string[] | undefined>;
  /**
   * The current job being viewed, which is used to generate the last breadcrumb item.
   */
  currentJob?: Job | null;
  /**
   * A boolean indicating whether to add spacing after the breadcrumbs. If true, a medium-sized spacing will be added after the breadcrumbs.
   */
  spacingAfter?: boolean;
}

export default function JobSearchBreadcrumbs({
  jobsPage,
  facets,
  querystring,
  currentJob,
  spacingAfter = false,
}: JobSearchBreadcrumbsProps) {
  const intl = useIntl();
  const jobsLabel = intl.formatMessage({
    id: 'jobs',
    defaultMessage: 'Jobs',
  });
  const breadcrumbItems = useMemo(() => {
    return getJobSearchBreadcrumbItems({
      jobsPage,
      jobsLabel,
      facets,
      querystring,
      currentJob,
    });
  }, [currentJob, facets, jobsLabel, jobsPage, querystring]);

  if (breadcrumbItems.length <= 1) {
    // If there are no breadcrumb items or only the "Jobs" item,
    // we don't render the breadcrumbs.
    return null;
  }

  return (
    <>
      <Breadcrumbs items={breadcrumbItems} />
      {spacingAfter ? <Spacing size="medium" /> : null}
    </>
  );
}

export { JobSearchBreadcrumbs, hasJobSearchBreadcrumbItems };
